import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Code, Brain, Shield, Settings, Play, Database, Zap, Terminal } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function CursorLanding() {
  const { t } = useTranslation('integrations');
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Create Cursor Integration in Memara
Use the Integrations UI to create a Cursor integration, then complete OAuth in Cursor.

# 2. Configure Cursor MCP (Streamable HTTP)
Add this to your MCP settings:
{
  "mcpServers": {
    "memara-cursor": {
      "transport": {
        "type": "http",
        "url": "<paste the MCP URL from Memara setup>"
      }
    }
  }
}

# 3. Complete OAuth in Cursor
On first tool call, Cursor will open the OAuth flow and bind to your selected Memara integration.

# 4. Available MCP Tools
- store_memory
- search_memories
- list_memories
- get_memory
- update_memory
- delete_memory
- get_server_info`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=example', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="cursor"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 shadow" aria-label="Cursor Logo">
          <Code className="w-8 h-8 text-blue-600" />
        </span>
      }
      title="Cursor Integration"
      description="AI-powered code editor integration with persistent memory for enhanced development workflows. Cursor IDE remembers your coding patterns, project context, and development decisions to provide intelligent code assistance and suggestions."
      features={[
        { icon: <Code className="h-4 w-4 text-blue-600" />, title: 'Intelligent Code Memory', description: 'Remember code snippets, patterns, and project-specific solutions for enhanced development efficiency.' },
        { icon: <Brain className="h-4 w-4 text-blue-600" />, title: 'Context-Aware Assistance', description: 'AI-powered code suggestions based on your project history and coding patterns stored in memory.' },
        { icon: <Shield className="h-4 w-4 text-blue-600" />, title: 'Secure Development', description: 'Secure authentication and memory isolation for enterprise development environments.' },
        { icon: <Database className="h-4 w-4 text-blue-600" />, title: 'Project Memory Spaces', description: 'Organize coding memories by project with secure space isolation and team collaboration.' },
        { icon: <Zap className="h-4 w-4 text-blue-600" />, title: 'Real-time Code Intelligence', description: 'Instant memory retrieval and code suggestions with sub-second response times.' },
        { icon: <Settings className="h-4 w-4 text-blue-600" />, title: 'Flexible Integration', description: 'Customizable permissions and memory scoping to match your development workflow.' },
      ]}
      setupSteps={[
        'Create a Cursor integration in your memory space',
        'Copy the Streamable HTTP MCP config to Cursor',
        'Complete OAuth authorization on first use',
        'Start coding with memory-powered assistance',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: t('wizard.cta.installWithWizard'),
          onClick: () => { window.location.href = '/wizard'; },
          className: 'bg-blue-50 text-blue-700 hover:bg-blue-100 px-8 py-4 h-11 rounded-md text-base border border-blue-200',
          icon: <Terminal className="w-4 h-4 mr-2" />,
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-blue-200 text-blue-600 hover:bg-blue-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-blue-200 text-blue-600 hover:bg-blue-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Settings className="w-4 h-4 mr-2" />,
        },
      ]}
      screenshots={[
        {
          id: 'cursor-integration-setup',
          title: 'Create Cursor Integration',
          description: 'Set up your Cursor integration with configurable permissions and project scoping',
          placeholder: 'Integration setup modal showing Cursor configuration options',
          badge: 'Step 1'
        },
        {
          id: 'cursor-ide-integration',
          title: 'Cursor IDE Integration',
          description: 'Connect Cursor IDE to your Memara memory system for intelligent assistance',
          placeholder: 'Cursor IDE interface showing memory integration and code suggestions',
          badge: 'Setup'
        },
        {
          id: 'cursor-memory-assistance',
          title: 'Memory-Powered Coding',
          description: 'Get intelligent code suggestions based on your project history and patterns',
          placeholder: 'Cursor IDE showing memory-powered code completion and suggestions',
          badge: 'Ready'
        }
      ]}
    />
  );
}
